import React, { FC, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { skipToken } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';

import { INotificationUserEmployeeDTO } from 'src/api/dtos';
import NotificationsRepository from 'src/api/repositories/common/v1/notifications.repository';
import UsersRepository from 'src/api/repositories/common/v1/users.repository';
import { IError } from 'src/api/types';
import {
  useMessageCenterEdit,
  useMessageCenterQuery,
  useMessageCenterSelection,
} from 'src/features/message-center/providers';
import { MailboxFolderEnums } from 'src/shared/enums';
import { useInfiniteScroll } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';

import { MessageCenterDetailsEdit } from './MessageCenterDetailsEdit';
import { MessageCenterDetailsEmpty } from './MessageCenterDetailsEmpty';
import { MessageCenterDetailsRead } from './MessageCenterDetailsRead';

export const MessageCenterDetails: FC = () => {
  const { t } = useTranslate();

  const { filters } = useMessageCenterQuery();
  const { selected, onClear } = useMessageCenterSelection();
  const { inputs } = useMessageCenterEdit();

  const [updateNotifications] = NotificationsRepository.endpoints.useUpdateNotificationsMutation();

  const {
    currentData: notificationMessage,
    isLoading,
    isFetching,
    error,
  } = NotificationsRepository.endpoints.useGetNotificationQuery(selected?.id ?? skipToken, { skip: !selected });

  const {
    collection: recipients,
    paginatedData: recipientsPaginatedData,
    handleLoadMore,
    isUninitialized,
    isLoadingMore,
    isRefreshing,
  } = useInfiniteScroll<INotificationUserEmployeeDTO>({
    useQuery: UsersRepository.endpoints.useGetUsersQuery,
    apiParams: { perPage: 10, notificationId: notificationMessage?.id, skip: !notificationMessage },
  });

  useEffect(() => {
    // Use cleanup callback to trigger mark as read of previous notification
    return () => {
      if (filters.mailboxFolder === MailboxFolderEnums.OUT) {
        return;
      }

      if (!notificationMessage || notificationMessage.isRead) {
        return;
      }

      const handleMarkAsRead = async () => {
        try {
          await updateNotifications({ ids: [notificationMessage.id], read: true }).unwrap();
        } catch (error) {
          const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
          toast.info(errorMessage, { type: toast.TYPE.ERROR });
        }
      };

      handleMarkAsRead();
    };
  }, [notificationMessage]);

  const loadingMessage = isLoading || isFetching;

  if (inputs) {
    return <MessageCenterDetailsEdit />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ alignSelf: 'flex-start', width: '100%' }}>
        {t('global.oopsAnErrorOccurred')}
      </Alert>
    );
  }

  if (loadingMessage || isUninitialized || isRefreshing) {
    return <Skeleton variant="rounded" animation="wave" width="100%" height="100%" />;
  }

  if (!notificationMessage || !recipients.length) {
    return <MessageCenterDetailsEmpty />;
  }

  return (
    <MessageCenterDetailsRead
      notificationMessage={notificationMessage}
      recipients={recipients}
      recipientsCount={recipientsPaginatedData.total}
      loading={isLoadingMore}
      onClose={onClear}
      onLoadMore={handleLoadMore}
    />
  );
};
